
import { Avatar } from '@univdiam/ui/components/avatar';
import SectionHeader from './SectionHeader'
import type { ActionType, OrderActivity } from '@univdiam/ui/type';
import { AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import type { JSX } from 'react';
import { LoaderCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { formatTimeInUserTimezone } from '@univdiam/ui/lib/formatedDates';
import { options } from '@univdiam/ui/lib/StatusOptions';

type TimelineLogProps = {
  Timeline: OrderActivity[];
  isAdmin: boolean | null,
  isLoading: boolean
}



type TemplateInput = {
  name?: string;
  metadata: Record<string, any>; // simple and flexible
};

const activityTemplates: Record<ActionType, (input: TemplateInput) => JSX.Element> = {
  order_created: ({ name = "Someone", metadata }) => (
    <p className='body2 '>
      {name} created an order <span className="font-semibold ">“{metadata?.job_title || "Untitled"}”</span>
    </p>
  ),

  status_updated: ({ name = "Someone", metadata }) => (
    <p className='body2'>
      {name} changed the order status to <span className="font-semibold">“{options.find(o => o.id === metadata?.status)?.label || "Unknown"}”</span>
    </p>
  ),
  additional_instruction_added: ({ name = "Someone", metadata }) => (
    <p className="body2">
      {name} added a <span className="font-semibold">Special Instruction</span> under label <span className="font-semibold">“{metadata?.label || "Unknown"}”</span>
    </p>
  ),
  additional_instruction_deleted: ({ name = "Someone", metadata }) => (
    <p className="body2">
      {name} removed a <span className="font-semibold">Special Instruction</span> under label <span className="font-semibold">“{metadata?.label || "Unknown"}”</span>
    </p>
  ),
  additional_instruction_updated: ({ name = "Someone", metadata }) => (
    <p className="body2">
      {name} updated a <span className="font-semibold">Special Instruction</span> under label {metadata?.previousLable !== metadata?.newLabel ? (
        <>
          <span className="font-semibold">“{metadata?.previousLable || "Unknown"}”</span> to{" "}
          <span className="font-semibold">“{metadata?.newLabel || "Unknown"}”</span>
        </>
      ) : (
        <span className="font-semibold">“{metadata?.newLabel || "Unknown"}”</span>
      )}
    </p>
  ),
  cad_added: ({ name = "Someone" }) => (
    <p className="body2">
      {name} added a <span className="font-semibold">CAD</span>
    </p>
  ),
  cad_order_created: ({ name = "Someone" }) => (
    <p className="body2">
      {name} submitted a CAD order
    </p>
  ),
  chat_message: ({ name = "Someone" }) => (
    <p className="body2">
      {name} sent a message
    </p>
  ),
};


function formatActivity(activity: OrderActivity) {
  const { action_type, action_by, metadata, customer_name, admin_name } = activity;

  const name = (action_by === 'CUSTOMER' || action_by === 'STAFF') ? customer_name : admin_name || 'Someone';

  const templateFn = activityTemplates[action_type];

  return templateFn ? templateFn({ name, metadata }) : `${name} performed an unknown action`;
}


function TimelineLogSection({ Timeline, isAdmin, isLoading }: TimelineLogProps) {
  return (
    <div className='flex flex-col bg-white border border-[#E9EAEB] rounded-md text-caption1 overflow-hidden h-full'>
      <SectionHeader
        title="Timeline Log"
        sectionIcon={<Clock strokeWidth={1.5} className="w-5 h-5 text-gray-600" />}
        isAdmin={isAdmin}
      />
      <div className="pb-3 px-4 flex flex-col overflow-y-auto">
        <div className='text-body2 flex flex-col gap-2'>
          <div className='flex items-center justify-center'>
            {isLoading && <LoaderCircle className='animate-spin' />}
          </div>
          <div className='flex items-center justify-center'>
            {!isLoading && Timeline.length === 0 && <p>No activity yet</p>}
          </div>
          {Timeline.map((log, index) => (
            <div key={index} className='flex items-start gap-[8px]'>
              <Avatar className='w-[20px] h-[20px] rounded-[2px] shrink-0'>
                <AvatarImage src={log?.customer_avatar || log?.admin_avatar} alt={log?.customer_name || log?.admin_name} className='w-full rounded-[2px] h-full' />
                <AvatarFallback className='flex items-center justify-center bg-gray-500 text-white w-full rounded-[2px] h-full'>{log?.customer_name?.[0] || log?.admin_name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 w-full">
                <div className="break-words w-full py-1">{formatActivity(log)}</div>
                <p className='caption text-[#777777] break-words'>
                  {format(new Date(log.created_at), 'dd MMM yyyy')} &#x2022; {formatTimeInUserTimezone(log.created_at)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default TimelineLogSection